/**
 * Learning Hooks
 *
 * TanStack Query hooks for learning materials and assignments:
 * - List/create/update/delete materials
 * - Upload materials to S3
 * - List/create/delete assignments
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

// ============================================
// TYPES
// ============================================

export interface LearningMaterial {
  id: string;
  title: string;
  description: string | null;
  materialType: 'video' | 'document';
  duration: number | null;
  isActive: boolean;
  generatedBy: string;
  createdAt: string;
  sop: { id: string; title: string } | null;
  assignmentCount: number;
  completionRate: number | null;
}

export interface LearningMaterialDetail extends LearningMaterial {
  updatedAt: string;
  downloadUrl: string;
  downloadUrlExpiresIn: number;
  assignmentStats: {
    total: number;
    completed: number;
    inProgress: number;
    completionRate: number | null;
  };
}

export interface LearningAssignment {
  id: string;
  operator: {
    id: string;
    firstName: string;
    lastName: string;
    employeeId: string;
  };
  material: {
    id: string;
    title: string;
    materialType: 'video' | 'document';
    duration: number | null;
  };
  assignedBy: {
    id: string;
    fullName: string;
  };
  dueDate: string | null;
  status: 'assigned' | 'in_progress' | 'completed';
  progress: number;
  completedAt: string | null;
  createdAt: string;
  isOverdue: boolean;
}

export interface CreateMaterialInput {
  title: string;
  description?: string;
  materialType: 'video' | 'document';
  sopId?: string;
  fileName: string;
  contentType: string;
  duration?: number;
}

export interface CreateMaterialResponse {
  material: {
    id: string;
    title: string;
    materialType: string;
    s3Key: string;
  };
  upload: {
    url: string;
    expiresIn: number;
  };
}

export interface AssignMaterialInput {
  materialId: string;
  operatorIds: string[];
  dueDate?: Date;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ============================================
// QUERY KEYS
// ============================================

export const learningKeys = {
  all: ['learning'] as const,
  materials: () => [...learningKeys.all, 'materials'] as const,
  materialList: (filters: Record<string, unknown>) => [...learningKeys.materials(), 'list', filters] as const,
  materialLookup: () => [...learningKeys.materials(), 'lookup'] as const,
  materialDetail: (id: string) => [...learningKeys.materials(), 'detail', id] as const,
  assignments: () => [...learningKeys.all, 'assignments'] as const,
  assignmentList: (filters: Record<string, unknown>) => [...learningKeys.assignments(), 'list', filters] as const,
  assignmentDetail: (id: string) => [...learningKeys.assignments(), 'detail', id] as const,
};

// ============================================
// MATERIALS QUERIES
// ============================================

export interface MaterialListOptions {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  sopId?: string;
  materialType?: 'video' | 'document';
  isActive?: boolean;
  search?: string;
}

/**
 * Fetch materials list with pagination and filters
 */
export function useMaterials(options?: MaterialListOptions) {
  const api = useApi();

  return useQuery({
    queryKey: learningKeys.materialList((options ?? {}) as Record<string, unknown>),
    queryFn: async () => {
      const params = new URLSearchParams();

      if (options?.page) params.set('page', String(options.page));
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.sortBy) params.set('sortBy', options.sortBy);
      if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
      if (options?.sopId) params.set('sopId', options.sopId);
      if (options?.materialType) params.set('materialType', options.materialType);
      if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
      if (options?.search) params.set('search', options.search);

      const queryString = params.toString();
      const endpoint = queryString ? `/api/learning/materials?${queryString}` : '/api/learning/materials';

      return api.get<PaginatedResponse<LearningMaterial>>(endpoint);
    },
  });
}

/**
 * Fetch materials for dropdown (lightweight)
 */
export function useMaterialsLookup() {
  const api = useApi();

  return useQuery({
    queryKey: learningKeys.materialLookup(),
    queryFn: () =>
      api.get<{ id: string; title: string; materialType: 'video' | 'document' }[]>(
        '/api/learning/materials/lookup'
      ),
  });
}

/**
 * Fetch single material details
 */
export function useMaterial(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: learningKeys.materialDetail(id ?? ''),
    queryFn: () => api.get<LearningMaterialDetail>(`/api/learning/materials/${id}`),
    enabled: !!id,
  });
}

// ============================================
// MATERIALS MUTATIONS
// ============================================

/**
 * Create material (returns presigned upload URL)
 */
export function useCreateMaterial() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMaterialInput) =>
      api.post<CreateMaterialResponse>('/api/learning/materials', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.materials() });
    },
  });
}

/**
 * Upload file to S3 using presigned URL
 */
export function useUploadFile() {
  return useMutation({
    mutationFn: async ({ url, file, onProgress }: { url: string; file: File; onProgress?: (percent: number) => void }) => {
      return new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onProgress) {
            const percent = Math.round((event.loaded / event.total) * 100);
            onProgress(percent);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Upload failed')));

        xhr.open('PUT', url);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
    },
  });
}

/**
 * Confirm upload complete
 */
export function useConfirmUpload() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, duration }: { id: string; duration?: number }) =>
      api.post(`/api/learning/materials/${id}/confirm-upload`, { duration }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.materials() });
    },
  });
}

/**
 * Update material
 */
export function useUpdateMaterial() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; title?: string; description?: string | null; sopId?: string | null; isActive?: boolean }) =>
      api.put(`/api/learning/materials/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: learningKeys.materials() });
      queryClient.invalidateQueries({ queryKey: learningKeys.materialDetail(variables.id) });
    },
  });
}

/**
 * Delete material (soft delete)
 */
export function useDeleteMaterial() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/learning/materials/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.materials() });
    },
  });
}

// ============================================
// ASSIGNMENTS QUERIES
// ============================================

export interface AssignmentListOptions {
  page?: number;
  limit?: number;
  operatorId?: string;
  materialId?: string;
  status?: 'assigned' | 'in_progress' | 'completed';
  overdue?: boolean;
}

/**
 * Fetch assignments list with filters
 */
export function useAssignments(options?: AssignmentListOptions) {
  const api = useApi();

  return useQuery({
    queryKey: learningKeys.assignmentList((options ?? {}) as Record<string, unknown>),
    queryFn: async () => {
      const params = new URLSearchParams();

      if (options?.page) params.set('page', String(options.page));
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.operatorId) params.set('operatorId', options.operatorId);
      if (options?.materialId) params.set('materialId', options.materialId);
      if (options?.status) params.set('status', options.status);
      if (options?.overdue !== undefined) params.set('overdue', String(options.overdue));

      const queryString = params.toString();
      const endpoint = queryString ? `/api/learning/assignments?${queryString}` : '/api/learning/assignments';

      return api.get<PaginatedResponse<LearningAssignment>>(endpoint);
    },
  });
}

/**
 * Fetch single assignment details
 */
export function useAssignment(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: learningKeys.assignmentDetail(id ?? ''),
    queryFn: () => api.get<LearningAssignment>(`/api/learning/assignments/${id}`),
    enabled: !!id,
  });
}

// ============================================
// ASSIGNMENTS MUTATIONS
// ============================================

/**
 * Create assignments (bulk)
 */
export function useCreateAssignments() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AssignMaterialInput) =>
      api.post<{ created: number; skipped: number; assignments: { id: string; operatorId: string; dueDate: string | null }[] }>(
        '/api/learning/assignments',
        {
          ...data,
          dueDate: data.dueDate?.toISOString(),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.assignments() });
      queryClient.invalidateQueries({ queryKey: learningKeys.materials() });
    },
  });
}

/**
 * Update assignment (due date)
 */
export function useUpdateAssignment() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dueDate }: { id: string; dueDate?: Date | null }) =>
      api.put(`/api/learning/assignments/${id}`, {
        dueDate: dueDate?.toISOString() ?? null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.assignments() });
    },
  });
}

/**
 * Delete assignment
 */
export function useDeleteAssignment() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/learning/assignments/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: learningKeys.assignments() });
      queryClient.invalidateQueries({ queryKey: learningKeys.materials() });
    },
  });
}
